import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Users, IdCard, FileText, UserCheck, TrendingUp, CreditCard, Clock, Lock, Euro } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageLoading, PageHeader } from "@/components/shared";
import api from "@/lib/api";

const COLORS = ["#06B6D4", "#0D9488", "#0A192F", "#D97706", "#DC2626", "#64748B"];

function ModuleStats({ t }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/module-stats").then((r) => setM(r.data)).catch(() => setM(null)); }, []);
  if (!m) return null;
  const cards = [
    ["Subunternehmer ARR", `€${(m.modules?.subcontractor?.arr_eur ?? m.arr_eur ?? 0).toLocaleString()}`],
    ["Zeiterfassung ARR", `€${(m.modules?.time_tracking?.arr_eur || 0).toLocaleString()}`],
    ["Combined Add-on ARR", `€${(m.combined_arr_eur || 0).toLocaleString()}`],
    [t("admModule.mActiveSubs"), m.active_subscriptions],
    [t("admModule.mPastDue"), m.past_due],
    [t("admModule.mSubscribed"), m.subscribed_organizations],
    [t("sub.tCompanies"), m.subcontractors],
    [t("sub.tWorkers"), m.subcontractor_workers],
    [t("admModule.mAiDocs"), m.ai_documents_processed],
  ];
  return (
    <div className="mt-8" data-testid="admin-module-stats">
      <h2 className="mb-3 font-heading text-lg font-bold text-navy">{t("admModule.moduleStatsTitle")}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([label, value], i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`module-stat-${i}`}>
            <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
            <div className="mt-2 font-heading text-2xl font-extrabold text-navy">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400">{label}</span><Icon className="h-4 w-4 text-cyan-dark" /></div>
      <div className="mt-2 font-heading text-3xl font-extrabold text-navy">{value}</div>
    </div>
  );
}

export default function AdminOverview() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <PageLoading />;
  const m = d.metrics;
  const pb = d.plan_breakdown || {};
  const everStarter = (pb.starter || 0) + (pb.starter_to_pro_upgrades || 0);
  const convRate = everStarter ? Math.round(((pb.starter_to_pro_upgrades || 0) / everStarter) * 100) : 0;
  return (
    <div data-testid="admin-overview">
      <PageHeader title={t("adm.ovTitle")} subtitle={t("adm.ovSubtitle")} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Metric icon={Building2} label={t("adm.mTotalCompanies")} value={m.total_companies} />
        <Metric icon={Building2} label={t("adm.mActiveCompanies")} value={m.active_companies} />
        <Metric icon={Users} label={t("adm.mTotalUsers")} value={m.total_users} />
        <Metric icon={Users} label={t("adm.mEmployers")} value={m.employers} />
        <Metric icon={UserCheck} label={t("adm.mWorkers")} value={m.workers} />
        <Metric icon={IdCard} label={t("adm.mEmployeeRecords")} value={m.total_employees} />
        <Metric icon={FileText} label={t("adm.mDocuments")} value={m.documents} />
        <Metric icon={UserCheck} label={t("adm.mActivePortals")} value={m.active_portals} />
        <Metric icon={TrendingUp} label={t("adm.mReg30")} value={m.registrations_30d} />
      </div>
      <ModuleStats t={t} />
      {d.revenue && (
        <div className="mt-8" data-testid="admin-revenue">
          <h2 className="mb-3 font-heading text-lg font-bold text-navy">{t("x.revenueTitle")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Metric icon={Euro} label={t("x.mrr")} value={"€" + d.revenue.mrr.toLocaleString()} />
            <Metric icon={TrendingUp} label={t("x.arr")} value={"€" + d.revenue.arr.toLocaleString()} />
            <Metric icon={CreditCard} label={t("x.addonArr")} value={"€" + d.revenue.addon_arr.toLocaleString()} />
            <Metric icon={Euro} label={t("x.totalArr")} value={"€" + d.revenue.total_arr.toLocaleString()} />
            <Metric icon={Building2} label={t("x.avgRev")} value={"€" + d.revenue.avg_revenue_per_company.toLocaleString()} />
          </div>
        </div>
      )}
      {d.plan_breakdown && (
        <div className="mt-8" data-testid="admin-plan-breakdown">
          <h2 className="mb-3 font-heading text-lg font-bold text-navy">{t("x.planBreakdown")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            <Metric icon={Users} label={t("x.starterCustomers")} value={d.plan_breakdown.starter} />
            <Metric icon={Users} label={t("x.proCustomers")} value={d.plan_breakdown.professional} />
            <Metric icon={Building2} label={t("x.proSub")} value={d.plan_breakdown.pro_subcontractor} />
            <Metric icon={Clock} label={t("x.proTime")} value={d.plan_breakdown.pro_time_tracking} />
            <Metric icon={IdCard} label={t("x.proBoth")} value={d.plan_breakdown.pro_both} />
            <Metric icon={TrendingUp} label={t("x.starterToPro")} value={d.plan_breakdown.starter_to_pro_upgrades} />
          </div>
        </div>
      )}
      {d.plan_breakdown && (
        <div className="mt-6 grid gap-6 lg:grid-cols-3" data-testid="admin-conversion">
          <div className="flex flex-col justify-center rounded-2xl border border-slate-200 bg-white p-6">
            <span className="text-xs uppercase tracking-wider text-slate-400">{t("x.convRate")}</span>
            <div className="mt-2 font-heading text-5xl font-extrabold text-cyan-dark" data-testid="conversion-rate">{convRate}%</div>
            <span className="mt-2 text-xs text-slate-400">{t("x.convHint")}</span>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
            <h3 className="mb-4 font-heading font-bold text-navy">{t("x.convTitle")}</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart layout="vertical" data={[
                { name: t("x.convStarterEver"), v: everStarter },
                { name: t("x.convUpgraded"), v: pb.starter_to_pro_upgrades || 0 },
              ]} margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={12} width={160} />
                <Tooltip />
                <Bar dataKey="v" radius={[0, 6, 6, 0]}><Cell fill="#0A192F" /><Cell fill="#06B6D4" /></Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {d.subscriptions && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-sub-stats">
          <Metric icon={CreditCard} label={t("x.subActive")} value={d.subscriptions.active} />
          <Metric icon={Clock} label={t("x.subTrialing")} value={d.subscriptions.trialing} />
          <Metric icon={Lock} label={t("x.subExpired")} value={d.subscriptions.expired} />
          <Metric icon={Euro} label={t("x.mrr")} value={"€" + d.subscriptions.mrr} />
        </div>
      )}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <h3 className="mb-4 font-heading font-bold text-navy">{t("adm.chCompanies")}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.companies_over_time}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="month" stroke="#94a3b8" fontSize={12} /><YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="count" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 3 }} /></LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-4 font-heading font-bold text-navy">{t("adm.chRoles")}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={d.roles_dist} dataKey="count" nameKey="role" cx="50%" cy="50%" outerRadius={75} label={(e) => e.role.replace("_", " ")}>{d.roles_dist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-3">
          <h3 className="mb-4 font-heading font-bold text-navy">{t("adm.chCompliance")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.compliance_dist}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="status" stroke="#94a3b8" fontSize={12} /><YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} /><Tooltip /><Bar dataKey="count" radius={[6, 6, 0, 0]}>{d.compliance_dist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
